import { Button, Card, CardContent, Container, Typography } from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import jsonp from "jsonp";
import { Helmet } from "react-helmet";
import React, { useEffect, useState } from "react";
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLoaderData,
  useRouteError,
  useSearchParams,
} from "react-router-dom";
import config from "./config";
import { TokenContext } from "./context/TokenContext";
import { MissingIntegrationPageSearchParams, TokenNotValid } from "./error/errors";
import { InSiteContext } from "./context/InSiteContext";
import BrochureForm from "./pages/BrochureForm";
import { LoaderData } from "./types/types";
import AfspraakForm from "./pages/AfspraakForm";

async function getIntegrationSettings(dataurl: string) {
  try {
    const integrationSettings = await new Promise((resolve, reject) => {
      jsonp(dataurl, {}, function (err: Error | null, data: { scriptUrl: string; cssUrl: string }) {
        if (err) {
          console.error(err.message);
          reject(err);
        } else {
          resolve({
            scriptUrl: data.scriptUrl,
            cssUrl: data.cssUrl,
          });
        }
      });
    }).then((res) => res as { scriptUrl: string; cssUrl: string });
    return integrationSettings;
  } catch (e) {
    console.warn(e);
  }
}

function ErrorBoundary() {
  const error = useRouteError();
  if (error instanceof TokenNotValid) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Card variant="outlined">
            <CardContent sx={{ minWidth: "300px", maxWidth: "550px" }}>
              <Typography variant="h5" component="h2" gutterBottom>
                AFAS Error
              </Typography>
              <Typography variant="body1">
                Er is een fout opgetreden bij het communiceren met AFAS Profit. Controleer of u verbonden bent
                met het internet en of uw inloggegevens correct zijn. Probeer het later opnieuw of neem
                contact op met de systeembeheerder als het probleem aanhoudt.
              </Typography>
              <Button
                sx={{ mt: 2 }}
                onClick={() => {
                  window.location.reload();
                }}
                variant="outlined"
              >
                Refresh pagina
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (error instanceof MissingIntegrationPageSearchParams) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Card variant="outlined">
            <CardContent sx={{ minWidth: "300px", maxWidth: "550px" }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Ontbrekende query parameters
              </Typography>
              <Typography variant="body1">
                Er ontbreken enkele query parameters om deze pagina te laden. Een mogelijke reden is dat de
                pagina niet via een integratie pagina wordt geladen. Controleer of u de juiste URL heeft
                gebruikt en of u alle benodigde parameters heeft ingevuld. Probeer het later opnieuw of neem
                contact op met de systeembeheerder als het probleem aanhoudt.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Card variant="outlined">
          <CardContent>Error</CardContent>
        </Card>
      </Box>
    </Container>
  );
}

// function LoadingPage() {
//   return (
//     <>
//       <Box mb={2}>
//         <LinearProgress />
//       </Box>
//     </>
//   );
// }

type IntegrationSettings = { scriptUrl: string; cssUrl: string };
type MaybePromise<T> = T | Promise<T>;

function isPromise<T>(value: MaybePromise<T>): value is Promise<T> {
  return (
    typeof value === "object" &&
    value !== null &&
    "then" in value &&
    typeof (value as { then: unknown }).then === "function"
  );
}

const GladiorWrapper: React.FC<{
  gladiorToken: MaybePromise<string>;
  integrationSettings: MaybePromise<IntegrationSettings | undefined>;
  insiteUrl: string;
  children: React.ReactNode;
}> = ({ gladiorToken, integrationSettings, insiteUrl, children }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();

  const [token, setToken] = useState<string>(isPromise(gladiorToken) ? "" : gladiorToken || "");
  const [settings, setSettings] = useState<IntegrationSettings | null>(
    isPromise(integrationSettings) ? null : (integrationSettings ?? null),
  );

  // Resolve token in the background and update query param when ready
  useEffect(() => {
    if (isPromise(gladiorToken)) {
      gladiorToken
        .then((t) => {
          setToken(t);
          const next = new URLSearchParams(window.location.search);
          next.set("gladiorToken", t);
          setSearchParams(next);
        })
        .catch((err) => {
          console.error("Error resolving gladiorToken", err);
        });
    } else if (gladiorToken) {
      setToken(gladiorToken);
      const next = new URLSearchParams(window.location.search);
      next.set("gladiorToken", gladiorToken);
      setSearchParams(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gladiorToken]);

  // Resolve integration settings in the background
  useEffect(() => {
    if (isPromise(integrationSettings)) {
      integrationSettings
        .then((s) => {
          if (s) {
            setSettings(s);
          }
        })
        .catch((err) => console.warn("Error resolving integrationSettings", err));
    } else if (integrationSettings) {
      setSettings(integrationSettings);
    }
  }, [integrationSettings]);

  return (
    <TokenContext.Provider value={token}>
      <InSiteContext.Provider value={insiteUrl}>
        {/* Once settings are loaded, inject external CSS/JS.
            This does NOT block rendering of the form. */}
        {settings && (
          <Helmet>
            <link rel="stylesheet" href={settings.cssUrl} type="text/css" />
            {/* <script src={settings.scriptUrl} type="text/javascript" /> */}
          </Helmet>
        )}

        {/* BrochureForm (Outlet) is always rendered, token/settings come in "later" */}
        {children}
      </InSiteContext.Provider>
    </TokenContext.Provider>
  );
};

function Wrapper() {
  const { gladiorToken, insiteUrl, integrationSettings } = useLoaderData() as LoaderData;

  return (
    <GladiorWrapper
      gladiorToken={gladiorToken}
      integrationSettings={integrationSettings}
      insiteUrl={insiteUrl}
    >
      <Outlet />
    </GladiorWrapper>
  );
}

async function getGladiorToken(tokenurl: string, code: string, sessionid: string, publickey: string) {
  try {
    const {
      data: { token },
    } = await axios.post(`${config.BASE_URL}token`, {
      tokenurl,
      code,
      sessionid,
      publickey,
    });
    return token;
  } catch (error) {
    throw new TokenNotValid("Token not valid");
  }
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={<Wrapper />}
      errorElement={<ErrorBoundary />}
      loader={async ({ request }) => {
        const searchParams = new URL(request.url).searchParams;
        const tokenurl = searchParams.get("tokenurl") ?? "";
        const code = searchParams.get("code") ?? "";
        const sessionid = searchParams.get("sessionid") ?? "";
        const publickey = searchParams.get("publickey") ?? "";
        const dataurl = searchParams.get("dataurl") ?? "";
        const gladiorToken = searchParams.get("gladiorToken");

        if (!tokenurl || !code || !sessionid || !publickey) {
          throw new MissingIntegrationPageSearchParams("Missing params");
        }

        const integrationSettings = getIntegrationSettings(dataurl);

        if (!gladiorToken) {
          const gladiorTokenResponse = getGladiorToken(tokenurl, code, sessionid, publickey);
          return {
            gladiorToken: gladiorTokenResponse,
            insiteUrl: tokenurl.replace("/integrationtoken", ""),
            integrationSettings,
          };
        } else {
          return {
            gladiorToken: gladiorToken,
            insiteUrl: tokenurl.replace("/integrationtoken", ""),
            integrationSettings,
          };
        }
      }}
    >
      <Route path="/:id" element={<BrochureForm />} />
      <Route path="/afspraak" element={<AfspraakForm />} />
    </Route>,
  ),
);

function Root() {
  return <RouterProvider router={router} />;
}

export default Root;
